import { BasicReference } from "./BasicReference.js";
import { isReadableReference } from "./typeGuards.js";
function getDerivedValue(options) {
    const { origin, derive, fallback } = options;
    const originValue = origin.value;
    if (originValue == null) {
        return fallback;
    }
    const derivedRef = derive(originValue);
    // Don't use the fallback value here.
    // We should return exactly what the derived reference returns.
    return derivedRef.value;
}
/**
 * @internal scope: workspace
 */
export class ReferenceUnwrapped extends BasicReference {
    /**
     * This retains a strong reference to the origin reference,
     * ensuring that it is not garbage collected.
     *
     * @internal scope: package
     */
    #origin$;
    #getDerivedReference;
    #fallback;
    #derived$ = undefined;
    #originSubscription = undefined;
    #derivedSubscription = undefined;
    constructor(options) {
        const { origin, derive, fallback, onUpdate, comparator } = options;
        super({
            initialValue: getDerivedValue(options),
            onUpdate,
            comparator,
        });
        this.#origin$ = origin;
        this.#getDerivedReference = derive;
        this.#fallback = fallback;
        // NOTE: The instance should subscribe to the origin reference immediately.
        //
        // To clarify, `Reference` instances are not bound to a specific host,
        // and they don't have a lifecycle. They should be setup immediately after being created.
        //
        // However, `State` instances are subscribed to based the host's lifecycle.
        // It's the responsibility of the controller managing the `State` instance to setup and teardown
        // subscriptions based on the host's lifecycle.
        this.#subscribeToOrigin();
    }
    #subscribeToOrigin() {
        if (this.#originSubscription)
            return;
        this.#originSubscription = this.#origin$.subscribe(this.#originObserver);
    }
    #unsubscribeFromOrigin() {
        if (!this.#originSubscription)
            return;
        this.#originSubscription.unsubscribe();
        this.#originSubscription = undefined;
    }
    #subscribeToDerived() {
        if (this.#derivedSubscription || !this.#derived$) {
            return;
        }
        this.#derivedSubscription = this.#derived$.subscribe(this.#derivedObserver);
    }
    #unsubscribeFromDerived() {
        if (!this.#derivedSubscription)
            return;
        this.#derivedSubscription.unsubscribe();
        this.#derivedSubscription = undefined;
    }
    subscription = {
        unsubscribe: () => {
            this.#unsubscribeFromOrigin();
            this.#unsubscribeFromDerived();
        },
    };
    #originObserver = {
        next: (originValue) => {
            this.#unsubscribeFromDerived();
            if (originValue == null) {
                // If the origin value is `undefined`, we should use the fallback value.
                this.set(this.#fallback);
                return;
            }
            this.#derived$ = this.#getDerivedReference(originValue);
            // There's no need to set the value here,
            // because the value will be set when the derived reference
            // is subscribed to.
            this.#subscribeToDerived();
        },
    };
    #derivedObserver = {
        next: (derivedValue) => {
            this.set(derivedValue);
        },
    };
}
/**
 * @internal scope: workspace
 */
// TODO: Avoid using `any` in the return type.
// It's used to avoid some unnecessary complexity in the type system.
// It neither affects the functionality nor development experience.
export function normalizeReferenceUnwrappedArgs(params) {
    if (params.length === 1) {
        const firstParam = params[0];
        if (isReadableReference(firstParam)) {
            return {
                origin: firstParam,
                derive: (reference) => reference,
            };
        }
        return firstParam;
    }
    const [origin, derive, fallback] = params;
    return { derive, origin, fallback };
}
